/**
 * Admin → Institutional referral rewards: the minimal manual fulfillment queue.
 * DARK: routes.jsx registers /admin/institutional-rewards and menu.jsx lists it
 * only when REACT_APP_INSTITUTIONAL_PROMOTION_ENABLED is "true".
 *
 * Fulfillment is manual — an admin sends the Amazon gift card or cash outside
 * QLearn, then records it here. There is no payment API call anywhere.
 *
 * The ROLE_ADMIN check below only decides what to render (and avoids calling
 * the API for non-admins). The server authorizes every request independently
 * and makes fulfill idempotent; this page never assumes it is the gate.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Paper, TextField, Typography } from "@material-ui/core";
import Menu from "../menu/menu";
import {
  FULFILLMENT_METHODS,
  FULFILLMENT_REFERENCE_MAX_LENGTH,
  fulfillAdminReward,
  INSTITUTIONAL_CODES,
  listAdminRewards,
} from "../../components/institutional/institutional-api";
import { formatCents, messageForCode } from "../../components/institutional/institutional-copy";
import { isInstitutionalPromotionEnabled } from "../../components/institutional/institutional-flags";
import "../../components/institutional/institutional.scss";

export const FULFILLED_STATUS = "FULFILLED";
export const REFERENCE_MAX_LENGTH = FULFILLMENT_REFERENCE_MAX_LENGTH;

const DENIED_CODES = [
  INSTITUTIONAL_CODES.FORBIDDEN,
  INSTITUTIONAL_CODES.UNAUTHORIZED,
  INSTITUTIONAL_CODES.ADMIN_REQUIRED,
  INSTITUTIONAL_CODES.AUTHENTICATION_REQUIRED,
];

const METHOD_LABELS = {
  AMAZON: "Amazon gift card",
  CASH: "Cash",
};

const isFulfilled = (item) => String(item?.status || "").toUpperCase() === FULFILLED_STATUS;

// Awaiting fulfillment first, then by milestone; stable otherwise.
const sortQueue = (items) => [...items].sort((a, b) => {
  const fa = isFulfilled(a) ? 1 : 0;
  const fb = isFulfilled(b) ? 1 : 0;
  if (fa !== fb) return fa - fb;
  return (Number(a.milestone) || 0) - (Number(b.milestone) || 0);
});

const RewardRow = ({ item, onFulfilled, onStale }) => {
  const [method, setMethod] = useState(FULFILLMENT_METHODS[0]);
  const [reference, setReference] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const trimmedReference = reference.trim();
  const referenceError = trimmedReference.length > REFERENCE_MAX_LENGTH
    ? `Keep the reference to ${REFERENCE_MAX_LENGTH} characters or fewer.`
    : null;

  const startConfirm = () => {
    if (!trimmedReference) {
      setError("Enter the order or transaction reference first.");
      return;
    }
    if (referenceError) return;
    setError(null);
    setConfirming(true);
  };

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const result = await fulfillAdminReward(item.id, { method, reference: trimmedReference });
    if (!mountedRef.current) return;
    setBusy(false);
    setConfirming(false);
    if (result.ok) {
      onFulfilled(item.id, result.data);
      return;
    }
    if (result.status === 409 || result.code === INSTITUTIONAL_CODES.CONFLICT
      || result.code === INSTITUTIONAL_CODES.ALREADY_FULFILLED) {
      // Already fulfilled (another admin, or a retried request) — show truth.
      // A same method + reference replay is a 200 from the server, not a 409.
      setError("This reward was already recorded. Refreshing the queue.");
      onStale();
      return;
    }
    setError(messageForCode(result.code, { context: "admin" }));
  };

  const amount = formatCents(item.amountCents);

  return (
    <tr data-testid={`reward-row-${item.id}`}>
      <td>{item.id}</td>
      <td>{item.referrerUserId}</td>
      <td>{item.milestone}</td>
      <td>{amount}</td>
      <td data-testid={`reward-status-${item.id}`}>{item.status}</td>
      <td>
        {isFulfilled(item) ? (
          // The fulfill response carries these; the list summary does not, so
          // a fulfilled row loaded from the list shows "Recorded".
          <Typography variant="caption" data-testid={`reward-fulfillment-${item.id}`}>
            {item.fulfillmentMethod
              ? `${METHOD_LABELS[item.fulfillmentMethod] || item.fulfillmentMethod}`
              : "Recorded"}
            {item.fulfillmentReference ? ` · ${item.fulfillmentReference}` : ""}
          </Typography>
        ) : (
          <Box className="institutional-panel">
            <label htmlFor={`reward-method-${item.id}`}>
              <Typography variant="caption">Method</Typography>
            </label>
            <select
              id={`reward-method-${item.id}`}
              value={method}
              onChange={(e) => { setMethod(e.target.value); setConfirming(false); }}
              disabled={busy}
              data-testid={`reward-method-${item.id}`}
            >
              {FULFILLMENT_METHODS.map((m) => (
                <option key={m} value={m}>{METHOD_LABELS[m] || m}</option>
              ))}
            </select>
            <TextField
              id={`reward-reference-${item.id}`}
              label="Order / transaction reference"
              value={reference}
              onChange={(e) => { setReference(e.target.value); setConfirming(false); }}
              error={!!referenceError}
              helperText={referenceError || "Never paste a gift card claim code."}
              inputProps={{ "data-testid": `reward-reference-${item.id}` }}
              disabled={busy}
              size="small"
              variant="outlined"
            />
            {confirming ? (
              <Box className="institutional-actions">
                <Typography variant="caption" data-testid={`reward-confirm-text-${item.id}`}>
                  Record {amount} sent via {METHOD_LABELS[method] || method} (ref {trimmedReference})?
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={submit}
                  disabled={busy}
                  data-testid={`reward-confirm-${item.id}`}
                >
                  {busy ? <CircularProgress size={16} color="inherit" /> : "Confirm"}
                </Button>
                <Button size="small" onClick={() => setConfirming(false)} disabled={busy}>
                  Cancel
                </Button>
              </Box>
            ) : (
              <Box className="institutional-actions">
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={startConfirm}
                  disabled={busy}
                  data-testid={`reward-fulfill-${item.id}`}
                >
                  Record fulfillment
                </Button>
              </Box>
            )}
            {error && (
              <Typography variant="caption" role="alert" className="institutional-error" data-testid={`reward-error-${item.id}`}>
                {error}
              </Typography>
            )}
          </Box>
        )}
      </td>
    </tr>
  );
};

export default function InstitutionalRewardsAdminPage() {
  const isAdmin = window.localStorage.getItem("role") === "ROLE_ADMIN";
  const enabled = isInstitutionalPromotionEnabled();
  const [state, setState] = useState({ status: "loading", items: [] });
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, status: "loading" }));
    const result = await listAdminRewards();
    if (!mountedRef.current) return;
    if (result.ok && Array.isArray(result.data?.items)) {
      setState({ status: "ready", items: result.data.items });
    } else {
      setState({ status: "error", items: [], code: result.ok ? INSTITUTIONAL_CODES.BAD_RESPONSE : result.code });
    }
  }, []);

  useEffect(() => {
    if (isAdmin && enabled) {
      load();
    }
  }, [isAdmin, enabled, load]);

  const handleFulfilled = (id, updated) => {
    if (updated && updated.id !== undefined && String(updated.id) === String(id)) {
      setState((prev) => ({
        ...prev,
        items: prev.items.map((item) => (String(item.id) === String(id) ? { ...item, ...updated } : item)),
      }));
    } else {
      load();
    }
  };

  let content;
  if (!enabled || !isAdmin) {
    content = (
      <Paper elevation={0} className="institutional-notice" data-testid="institutional-rewards-denied">
        <Typography variant="body1">You don&apos;t have access to this page.</Typography>
      </Paper>
    );
  } else if (state.status === "loading") {
    content = <CircularProgress size={24} data-testid="institutional-rewards-loading" />;
  } else if (state.status === "error") {
    const denied = DENIED_CODES.includes(state.code);
    content = (
      <Box className="institutional-panel" data-testid="institutional-rewards-error">
        <Typography variant="body2" role="alert">
          {denied
            ? "The server did not authorize this account to view the reward queue."
            : state.code === INSTITUTIONAL_CODES.NOT_FOUND
              ? "The institutional promotion is not enabled on the server."
              : messageForCode(state.code, { context: "admin" })}
        </Typography>
        {!denied && (
          <Box className="institutional-actions">
            <Button variant="text" color="primary" onClick={load}>Try again</Button>
          </Box>
        )}
      </Box>
    );
  } else if (state.items.length === 0) {
    content = (
      <Typography variant="body2" data-testid="institutional-rewards-empty">
        No rewards in the queue.
      </Typography>
    );
  } else {
    const awaiting = state.items.filter((item) => !isFulfilled(item)).length;
    content = (
      <>
        <Typography variant="body2" data-testid="institutional-rewards-summary">
          {awaiting} awaiting fulfillment · {state.items.length - awaiting} fulfilled
        </Typography>
        <Box style={{ overflowX: "auto" }}>
          <table className="institutional-rewards-table" data-testid="institutional-rewards-table">
            <thead>
              <tr>
                <th>Reward</th>
                <th>Referrer user</th>
                <th>Milestone</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Fulfillment</th>
              </tr>
            </thead>
            <tbody>
              {sortQueue(state.items).map((item) => (
                <RewardRow key={item.id} item={item} onFulfilled={handleFulfilled} onStale={load} />
              ))}
            </tbody>
          </table>
        </Box>
      </>
    );
  }

  return (
    <Box component="div" className="body-cont-over">
      <Menu />
      <Box component="main" style={{ padding: 24, width: "100%" }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Institutional referral rewards
        </Typography>
        {enabled && isAdmin && (
          <Typography variant="body2" className="institutional-intro" style={{ marginBottom: 16 }}>
            Rewards are fulfilled manually: send the Amazon gift card or cash outside QLearn, then record it here.
            The server checks your admin role on every request and records each reward at most once.
          </Typography>
        )}
        {content}
      </Box>
    </Box>
  );
}
